import { Observation } from "@/resources/types";
import React, { useCallback } from "react";


export type ObsTableCtxValue = {
  value: {
    hoveredElementIndex: number | null;
    observations: Observation[];
  },
  set: (v: ObsTableCtxValue["value"]) => unknown,
  getObservationByHoveredElemIndex: () => Observation | undefined;
  changeHoveredElementIndex: (index: number | null) => void;
}
export const defaultObsTableCtxValue: ObsTableCtxValue = {
  value: { hoveredElementIndex: null, observations: [] },
  set: console.log,
  getObservationByHoveredElemIndex: () => undefined,
  changeHoveredElementIndex: console.log,
};

export const ObsTableCtx = React.createContext(defaultObsTableCtxValue);
export const useObsTableCtx = () => React.useContext(ObsTableCtx);

type Props = {
  observations: Observation[] | undefined
}
export const WithObsTableCtx: React.FC<React.PropsWithChildren<Props>> = React.memo(p => {
  const [value, set] = React.useState({
    ...defaultObsTableCtxValue.value,
    observations: p.observations || [],
  });

  const getObservationByHoveredElemIndex = useCallback(() => {
    if (value.hoveredElementIndex === null || !p.observations) return;

    return p.observations[value.hoveredElementIndex];
  }, [value.hoveredElementIndex, p.observations]);

  const changeHoveredElementIndex = useCallback((index: number | null) => {
    set(s => ({ ...s, hoveredElementIndex: index }));
  }, [value.hoveredElementIndex, p.observations]);

  const ctxValue = React.useMemo(() => ({
    value, set, getObservationByHoveredElemIndex, changeHoveredElementIndex
  }), [value.hoveredElementIndex, p.observations, set, getObservationByHoveredElemIndex, changeHoveredElementIndex]);

  return (
    <ObsTableCtx.Provider value={ctxValue}>
      {p.children}
    </ObsTableCtx.Provider>
  );
});
WithObsTableCtx.displayName = "components/ObservationTable/WithObsTableCtx";
